import React from "react";
import { IUser } from "../api/types";
import { authApi } from "../api/authApi";

type State = {
  authUser: IUser | null;
  token: string | null;
};

type Action = {
  type: string;
  payload: IUser | string | null;
};

type Dispatch = (action: Action) => void;

const initialState: State = {
  authUser: null,
  token: null,
};

type StateContextProviderProps = { children: React.ReactNode };

const StateContext = React.createContext<
  { state: State; dispatch: Dispatch } | undefined
>(undefined);

const stateReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "SET_USER": {
      return {
        ...state,
        authUser: action.payload as IUser,
      };
    }
    case "SET_TOKEN": {
      if (action.payload) {
        localStorage.setItem("token", action.payload as string);
        authApi.defaults.headers.common["Authorization"] = `Bearer ${
          action.payload as string
        }`;
      }
      return {
        ...state,
        token: action.payload as string,
      };
    }
    case "RESET_USER": {
      localStorage.removeItem("token");
      authApi.defaults.headers.common["Authorization"] = undefined;
      return initialState;
    }
    default: {
      throw new Error(`Unhandled action type`);
    }
  }
};

const StateContextProvider = ({ children }: StateContextProviderProps) => {
  const [state, dispatch] = React.useReducer(stateReducer, initialState);
  const value = { state, dispatch };

  return (
    <StateContext.Provider value={value}>{children}</StateContext.Provider>
  );
};

const useStateContext = () => {
  const context = React.useContext(StateContext);

  if (context) {
    return context;
  }

  throw new Error(`useStateContext must be used within a StateContextProvider`);
};

export { StateContextProvider, useStateContext };
